/**
 * Mobile menu drill-down navigation.
 *
 * Turbo ships the mobile menu as inline accordions: tapping a parent slides its
 * children open underneath it. This turns it into stacked panels instead —
 * tapping a parent replaces the current list with that parent's children and
 * adds a back row — matching the reference navigation the client sent.
 *
 * It reads the markup the theme already renders (a parent is any `li` holding
 * its own `ul`), so the lists the mega menu injects at runtime
 * (`ul.mobile-mega-menu`, whose items sit inside a wrapper `div`) drill down the
 * same way with no Liquid changes. All events are delegated from `document`, so
 * nothing needs re-binding when the mega menu injects, when the header
 * re-renders in the theme editor, or after an InstantClick page swap.
 *
 * Styles live in custom.css under "Mobile menu — drill-down"; whether the drawer
 * opens on the first parent is the header section's "Open the first mobile menu
 * item by default" setting, passed through as data-gk-mm-open-first.
 */
(() => {
  const MENU = '#mobile_menu';
  const DRAWER = '[data-mobile-menu]';

  const CLASS = {
    hidden: 'gk-mm-item--hidden',
    open: 'gk-mm-item--open',
    parent: 'gk-mm-item--parent',
    panel: 'gk-mm-panel',
    panelActive: 'gk-mm-panel--active',
    arrow: 'gk-mm-arrow',
    back: 'gk-mm-back'
  };

  const ARROW_RIGHT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_LEFT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M20 12H5M11 6l-6 6 6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Panels currently drilled into, outermost first. Each entry is the parent
  // `li` whose child list is on screen.
  let stack = [];

  const childPanel = (li) =>
    li ? Array.prototype.find.call(li.children, (el) => el.tagName === 'UL') : null;

  const rowOf = (li) => (li ? Array.prototype.find.call(li.children, (el) => el.tagName === 'A') : null);

  // Items are not always direct children of their panel: the mega menu wraps
  // its blocks in `div[data-meganav-mobile]`.
  const itemsOf = (panel) =>
    panel ? Array.prototype.slice.call(panel.querySelectorAll(':scope > li, :scope > div > li')) : [];

  const panelOf = (li) => (li ? li.closest('ul') : null);

  const titleOf = (li) => {
    const row = rowOf(li);
    if (!row) return '';
    const title = row.querySelector('[data-nav-title], .mobile-menu-item-title');
    return ((title || row).textContent || '').trim();
  };

  /**
   * Flags every parent item so CSS can show a drill-down arrow, and swaps the
   * theme's accordion chevron for one that points the way it now navigates.
   */
  const markParents = (menu) => {
    menu.querySelectorAll('li').forEach((li) => {
      if (!childPanel(li)) return;
      li.classList.add(CLASS.parent);
      const row = rowOf(li);
      if (!row) return;
      let arrow = row.querySelector('.icon-down-arrow, .' + CLASS.arrow);
      if (!arrow) {
        arrow = document.createElement('span');
        arrow.className = 'right icon-down-arrow';
        row.appendChild(arrow);
      }
      if (arrow.classList.contains(CLASS.arrow)) return;
      arrow.classList.add(CLASS.arrow);
      arrow.innerHTML = ARROW_RIGHT;
    });
  };

  const backRowFor = (panel, label) => {
    let back = panel.querySelector(':scope > .' + CLASS.back);
    if (!back) {
      back = document.createElement('li');
      back.className = CLASS.back;
      back.innerHTML = '<button type="button" data-gk-mm-back>' + ARROW_LEFT + '<span></span></button>';
      panel.insertBefore(back, panel.firstChild);
    }
    back.querySelector('span').textContent = label;
    return back;
  };

  const scrollToTop = (menu) => {
    const container = menu.closest('[data-nav]') || menu;
    container.scrollTop = 0;
  };

  const openPanel = (li) => {
    const panel = childPanel(li);
    const parent = panelOf(li);
    if (!panel || !parent) return;
    itemsOf(parent).forEach((item) => {
      if (item !== li) item.classList.add(CLASS.hidden);
    });
    li.classList.add(CLASS.open);
    panel.classList.add(CLASS.panel, CLASS.panelActive);
    backRowFor(panel, titleOf(li));
    stack.push(li);
    scrollToTop(panel);
  };

  const closePanel = () => {
    const li = stack.pop();
    if (!li) return;
    const panel = childPanel(li);
    if (panel) panel.classList.remove(CLASS.panelActive);
    li.classList.remove(CLASS.open);
    itemsOf(panelOf(li)).forEach((item) => item.classList.remove(CLASS.hidden));
    scrollToTop(panelOf(li));
  };

  const reset = () => {
    while (stack.length) closePanel();
  };

  /**
   * The drawer is absolutely positioned and as tall as its content, which leaves
   * the page showing through under a short panel. Pinning it to the space left
   * below the header gives every panel the same full-screen background, and lets
   * a long list scroll inside the drawer instead of the page.
   */
  const setDrawerHeight = (drawer) => {
    const container = drawer.querySelector('[data-nav]');
    if (!container) return;
    const { top } = container.getBoundingClientRect();
    const height = Math.max(240, Math.round(window.innerHeight - top));
    container.style.setProperty('--gk-mm-height', height + 'px');
  };

  /**
   * "Open the first mobile menu item by default" in the header section settings.
   * On (the default), the drawer drills straight into the first parent so the
   * catalogue is one tap closer; off, it opens on the top-level list.
   */
  const opensFirstParent = (drawer) => drawer.dataset.gkMmOpenFirst !== 'false';

  const onDrawerOpen = (drawer) => {
    const menu = drawer.querySelector(MENU);
    if (!menu) return;
    setDrawerHeight(drawer);
    reset();
    markParents(menu);
    if (!opensFirstParent(drawer)) return;
    const first = itemsOf(menu).find((li) => childPanel(li));
    if (first) openPanel(first);
  };

  // `toggle` does not bubble, so listen in the capture phase to keep this
  // working across header re-renders.
  document.addEventListener(
    'toggle',
    (event) => {
      const drawer = event.target;
      if (!drawer || !drawer.matches || !drawer.matches(DRAWER)) return;
      if (drawer.open) {
        onDrawerOpen(drawer);
      } else {
        reset();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const back = event.target.closest(MENU + ' [data-gk-mm-back]');
    if (back) {
      event.preventDefault();
      closePanel();
      return;
    }

    const row = event.target.closest(MENU + ' li > a');
    if (!row) return;
    const li = row.parentElement;
    if (!childPanel(li)) return;

    // A parent that links somewhere keeps its link: only its arrow drills in.
    // Parents without one (`#`, empty) drill from anywhere on the row.
    const href = row.getAttribute('href');
    const navigates = href && href !== '/' && !href.includes('#');
    if (navigates && !event.target.closest('.' + CLASS.arrow)) return;

    event.preventDefault();
    openPanel(li);
  });

  window.addEventListener('resize', () => {
    const drawer = document.querySelector(DRAWER);
    if (drawer && drawer.open) setDrawerHeight(drawer);
  });
})();
